import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { LOCALE_ID, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule, UrlSerializer } from '@angular/router';
import { NewAssetDetailsComponent } from '@assetaccountant/assets/components/add-assets/new-asset-details/new-asset-details.component';
import { NewAssetFieldsFormComponent } from '@assetaccountant/assets/components/add-assets/new-asset-details/new-asset-fields-form/new-asset-fields-form.component';
import { DraftAssetListActionCellComponent } from '@assetaccountant/assets/components/add-assets/view-draft-assets2/draft-assets-list/cell-renderers/draft-asset-list-action-column.component';
import { DraftAssetListLinkCellComponent } from '@assetaccountant/assets/components/add-assets/view-draft-assets2/draft-assets-list/cell-renderers/draft-asset-list-link-column.component';
import { EditAssetDetailsComponent } from '@assetaccountant/assets/components/edit-asset-details/edit-asset-details.component';
import { EditAttachmentLinkModalComponent } from '@assetaccountant/assets/components/edit-attachment-link-modal/edit-attachment-link-modal.component';
import { EditFieldAllocationsComponent } from '@assetaccountant/assets/components/edit-field-allocations/edit-field-allocations.component';
import { TransactionDescriptionComponent } from '@assetaccountant/assets/components/view-asset/view-asset-transactions/transaction-description-cell-renderer/transaction-description-cell-renderer.component';
import { ViewAssetTransactionsComponent } from '@assetaccountant/assets/components/view-asset/view-asset-transactions/view-asset-transactions.component';
import { ViewAttachmentsComponent } from '@assetaccountant/assets/components/view-asset/view-attachments2/view-attachments2.component';
import { NameCellComponent } from '@assetaccountant/assets/components/view-assets/assets-grid/name-cell-renderer/name-cell-renderer.component';
import { StatusCellComponent } from '@assetaccountant/assets/components/view-assets/assets-grid/status-cell-renderer/status-cell-renderer.component';
import { SearchAssetsNameCellComponent } from '@assetaccountant/assets/components/view-assets/search-assets/search-assets-name-cell-renderer/search-assets-name-cell-renderer.component';
import { StoredReportListComponent } from '@assetaccountant/organisation-dashboard/components/register-list/consolidated-reports/stored-report-list.component';
import { RegisterListComponent } from '@assetaccountant/organisation-dashboard/components/register-list/register-list.component';
import { PoolTransactionDescriptionCellComponent } from '@assetaccountant/pools/components/view-pool-transactions/view-asset-transactions/transaction-description-cell-renderer/transaction-description-cell-renderer.component';
import { ViewPoolAssetTransactionsComponent } from '@assetaccountant/pools/components/view-pool-transactions/view-asset-transactions/view-pool-asset-transactions.component';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { LUCIDE_ICONS, LucideAngularModule, LucideIconProvider } from 'lucide-angular';
import { AvatarModule } from 'ngx-avatars';
import { MomentModule } from 'ngx-moment';
import { customIcons, icons } from '../../LucideIcons';
import { AlertBannerComponent } from './components/alert-banner/alert-banner.component';
import { AlertComponent } from './components/alert/alert.component';
import { AssetGroupSelectorComponent } from './components/asset-group-selector/asset-group-selector.component';
import { AzureMarketPlaceComponent } from './components/azure-market-place/azure-market-place.component';
import { ConfirmationModalComponent } from './components/confirmation-modal/confirmation-modal.component';
import { CurrencyInputComponent } from './components/currency-input/currency-input.component';
import { DateInputComponent } from './components/date-input/date-input.component';
import { DepreciationLookupModalComponent } from './components/depreciation-lookup-modal/depreciation-lookup-modal.component';
import { DepreciationLookupSearchItemSelectComponent } from './components/depreciation-lookup-search-item-select/depreciation-lookup-search-item-select.component';
import { DepreciationLookupSearchItemComponent } from './components/depreciation-lookup-search-item/depreciation-lookup-search-item.component';
import { DepreciationLookupComponent } from './components/depreciation-lookup/depreciation-lookup.component';
import { CaClass29MethodModifierComponent } from './components/depreciation-method-modifier/ca-class29-method-modifier/ca-class29-method-modifier.component';
import {
  DEPRECIATION_METHOD_MODIFIER,
  DepreciationMethodModifierComponent,
} from './components/depreciation-method-modifier/depreciation-method-modifier.component';
import { AutocalcLifeEndDateComponent } from './components/depreciation-method-selector/autocalc-life-end-date/autocalc-life-end-date.component';
import { AutocalcLifeComponent } from './components/depreciation-method-selector/autocalc-life/autocalc-life.component';
import { DepreciationMethodSelectorComponent } from './components/depreciation-method-selector/depreciation-method-selector.component';
import { ExpiredOrganisationInviteComponent } from './components/expired-organisation-invite/expired-organisation-invite.component';
import { ExportButtonComponent } from './components/export-button/export-button.component';
import { PaymentComponent } from './components/payment/payment.component';
import { PoolSettingSelectionComponent } from './components/pool-setting-selection/pool-setting-selection.component';

import { CustomFieldsEditSidePanelComponent } from './components/custom-fields/custom-fields-edit-side-panel/custom-fields-edit-side-panel.component';
import { CustomFieldsEditComponent } from './components/custom-fields/custom-fields-edit/custom-fields-edit.component';
import { CustomFieldsViewComponent } from './components/custom-fields/custom-fields-view/custom-fields-view.component';
import { DepreciationRuleSelectorComponent } from './components/depreciation-rule-selector/depreciation-rule-selector.component';
import { GridExportButtonDirective } from './components/export-button/export-button.directive';
import { ExportModalComponent } from './components/export-modal/export-modal.component';
import { FeatureLimitModalComponent } from './components/feature-limit-modal/feature-limit-modal.component';
import { FilterBarComponent } from './components/filter-bar/filter-bar.component';
import { FinYearSelectorComponent } from './components/fin-year-selector/fin-year-selector.component';
import { FormControlComponent } from './components/forms/form-control/form-control.component';
import { FormLabelComponent } from './components/forms/form-label/form-label.component';
import { GlAccountTypeaheadComponent } from './components/gl-account-typeahead/gl-account-typeahead.component';
import { GridExpandCollapseButtonComponent } from './components/grid-expand-collapse-button/grid-expand-collapse-button.component';
import { GridExpandCollapseButtonDirective } from './components/grid-expand-collapse-button/grid-expand-collapse-button.directive';
import { GridColumnGroupComponent } from './components/grid/grid-column-group/grid-column-group.component';
import { GridColumnHeaderComponent } from './components/grid/grid-column-header/grid-column-header.component';
import { GridColumnComponent } from './components/grid/grid-column/grid-column.component';
import { GridRowLinkComponent } from './components/grid/grid-row-link.component';
import { GridTableComponent } from './components/grid/grid-table/grid-table.component';
import { IntuitCopyrightComponent } from './components/intuit-copyright/intuit-copyright.component';
import { LoadingComponent } from './components/loading/loading.component';
import { AddPaymentMethodModalComponent } from './components/navigation/add-payment-method-modal/add-payment-method-modal.component';
import { CalendarSelectorComponent } from './components/navigation/calendar-selector/calendar-selector.component';
import { MonthYearDateSelectorComponent } from './components/navigation/calendar-selector/month-year-date-selector/month-year-date-selector.component';
import { SelectPeriodModalComponent } from './components/navigation/calendar-selector/select-period-modal/select-period-modal.component';
import { CreateOrganisationConfirmModalComponent } from './components/navigation/create-organisation-confirm-modal/create-organisation-confirm-modal.component';
import { CreateRegisterModalComponent } from './components/navigation/create-register-modal/create-register-modal.component';
import { EditOrganisationModalComponent } from './components/navigation/edit-organisation-modal/edit-organisation-modal.component';
import { FySelectorComponent } from './components/navigation/fy-selector/fy-selector.component';
import { HeaderBarComponent } from './components/navigation/header-bar/header-bar.component';
import { NavigationV2Component } from './components/navigation/navigation-v2/navigation-v2.component';
import { SubNavigationComponent } from './components/navigation/navigation-v2/sub-navigation/sub-navigation.component';
import { RegimeSelectorComponent } from './components/navigation/regime-selector/regime-selector.component';
import { SwitchOrganisationModalComponent } from './components/navigation/switch-organisation-modal/switch-organisation-modal.component';
import { ConfirmChangePlanModalComponent } from './components/navigation/view-pricing-modal/confirm-change-plan-modal/confirm-change-plan-modal.component';
import { PricingPackageActionsComponent } from './components/navigation/view-pricing-modal/pricing-package-actions/pricing-package-actions.component';
import { ViewPricingModalComponent } from './components/navigation/view-pricing-modal/view-pricing-modal.component';
import { OffersComponent } from './components/offers/offers.component';
import { CreateFirstOrganisationComponent } from './components/onboarding/create-first-organisation/create-first-organisation.component';
import { DemoBannerComponent } from './components/onboarding/demo-banner/demo-banner.component';
import { GuideBannerComponent } from './components/onboarding/guide-banner/guide-banner.component';
import { SettingsBannerComponent } from './components/onboarding/settings-banner/settings-banner.component';
import { StartBannerComponent } from './components/onboarding/start-banner/start-banner.component';
import { OnboardingQuestionsComponent } from './components/onboarding/start/onboarding-questions/onboarding-questions.component';
import { ReviewsComponent } from './components/onboarding/start/reviews/reviews.component';
import { StartComponent } from './components/onboarding/start/start.component';
import { OutageComponent } from './components/outage/outage.component';
import { PaymentRequiredComponent } from './components/payment-required/payment-required.component';
import { PercentageInputComponent } from './components/percentage-input/percentage-input.component';
import { PeriodSelectorComponent } from './components/period-selector/period-selector.component';
import { ProgressModalComponent } from './components/progress-modal/progress-modal.component';
import { ProgressRingComponent } from './components/progress-ring/progress-ring.component';
import { RegimesInputComponent } from './components/regimes-input/regimes-input.component';
import { ReleaseNotesModalComponent } from './components/release-notes-modal/release-notes-modal.component';
import { SearchBarComponent } from './components/search-bar/search-bar.component';
import { SidePanelComponent } from './components/side-panel/side-panel.component';
import { SimpleModalComponent } from './components/simple-modal/simple-modal.component';
import { SubscriptionLimitComponent } from './components/subscription-limit/subscription-limit.component';
import { SubscriptionCalculatorComponent } from './components/subscription/subscription-calculator/subscription-calculator.component';
import { SubscriptionPackagesComponent } from './components/subscription/subscription-packages/subscription-packages.component';
import { TaxOfficeAbbrComponent } from './components/tax-office-abbr/tax-office-abbr.component';
import { UpdateModalComponent } from './components/update-modal/update-modal.component';
import {
  AuthorizeEnableControlDirective,
  AuthorizeEnableDirective,
  AuthorizeEnableNavItemDirective,
} from './directives/authorize-enable.directive';
import { AuthorizeFeatureFlagDirective } from './directives/authorize-feature-flag.directive';
import { AuthorizeFeatureDirective } from './directives/authorize-feature.directive';
import { AuthorizeOrganisationAnyDirective } from './directives/authorize-organisation-any.directive';
import { AuthorizeOrganisationDirective } from './directives/authorize-organisation.directive';
import { AuthorizeRegisterAnyDirective } from './directives/authorize-register-any.directive';
import { AuthorizeRegisterFeatureFlagInvertedDirective } from './directives/authorize-register-feature-flag-inverted.directive';
import { AuthorizeRegisterFeatureFlagDirective } from './directives/authorize-register-feature-flag.directive';
import { AuthorizeRegisterDirective } from './directives/authorize-register.directive';
import { AuthorizeShowDirective } from './directives/authorize-show.directive';
import { BackgroundProgressDirective } from './directives/background-progress.directive';
import {
  ControlEnableContainerDirective,
  ControlEnableControlDirective,
  ControlEnableControlNameDirective,
} from './directives/control-enable.directive';
import { DragSortDirective } from './directives/drag-sort.directive';
import { FlagInputDirective } from './directives/forms/flag-input.directive';
import { FormControlClassDirective } from './directives/forms/form-control-class.directive';
import { InvalidMessageForDirective } from './directives/forms/invalid-message-for.directive';
import { MaxDateValidator } from './directives/forms/max-date.validator';
import { MinDateValidator } from './directives/forms/min-date.validator';
import { PrecisionDirective } from './directives/forms/precision.directive';
import { ValidSubmitDirective } from './directives/forms/valid-submit.directive';
import { ValidationContextDirective } from './directives/forms/validation-context.directive';
import { GridRowDataDirective } from './directives/grid/row-data.directive';
import { ServerRowDataDirective } from './directives/grid/server-row-data.directive';
import { ValueFormatDirective } from './directives/grid/value-format.directive';
import { InputNumberDirective } from './directives/input-number.directive';
import { ObserveResizeDirective } from './directives/observe-resize.directive';
import { TabHasChangesDirective } from './directives/tab-has-changes.directive';
import { TabRouteDirective } from './directives/tab-route.directive';
import { TrackingEventDirective } from './directives/tracking-event.directive';
import { VirtualConfigDirective } from './directives/virtual-config.directive';
import { AaCurrencyPipe } from './pipes/aa-currency.pipe';
import { AaDateRangePipe } from './pipes/aa-date-range.pipe';
import { AaDatePipe } from './pipes/aa-date.pipe';
import { AaFinYearPipe } from './pipes/aa-fin-year.pipe';
import { AaListDatePipe } from './pipes/aa-list-date.pipe';
import { AaPercentPipe } from './pipes/aa-percent.pipe';
import { EnumToArrayPipe } from './pipes/enum-to-array.pipe';
import { FilterArgumentPipe } from './pipes/filter-argument-pipe';
import { IzePipe } from './pipes/ize.pipe';
import { KeepHtmlPipe } from './pipes/keep-html.pipe';
import { ListPipe } from './pipes/list.pipe';
import { QuotePipe } from './pipes/quote.pipe';
import { AAUrlSerializer } from './services/aa-url-serializer';
import {
  SEARCH_EQUALITY,
  SORT_COMPARE,
  searchEqualityFactory,
  sortCompareFactory,
} from './services/compare.factory';
import { StandardPageActionsComponent } from './templates/standard-page-actions/standard-page-actions.component';
import { StandardPageContentActionsComponent } from './templates/standard-page-content-actions/standard-page-content-actions.component';
import { StandardPageContentGridComponent } from './templates/standard-page-content-grid/standard-page-content-grid.component';
import { StandardPageContentComponent } from './templates/standard-page-content/standard-page-content.component';
import { StandardPageComponent } from './templates/standard-page/standard-page.component';

@NgModule({
  imports: [
    AvatarModule,
    CommonModule,
    FormsModule,
    LucideAngularModule.pick(icons),
    MomentModule,
    NgbModule,
    ReactiveFormsModule,
    RouterModule,
    ScrollingModule,
  ],
  declarations: [
    AaCurrencyPipe,
    AaDatePipe,
    AaDateRangePipe,
    AaFinYearPipe,
    AaListDatePipe,
    AaPercentPipe,
    AddPaymentMethodModalComponent,
    AlertBannerComponent,
    AlertComponent,
    AssetGroupSelectorComponent,
    AuthorizeEnableControlDirective,
    AuthorizeEnableDirective,
    AuthorizeEnableNavItemDirective,
    AuthorizeFeatureDirective,
    AuthorizeFeatureFlagDirective,
    AuthorizeOrganisationAnyDirective,
    AuthorizeOrganisationDirective,
    AuthorizeRegisterAnyDirective,
    AuthorizeRegisterDirective,
    AuthorizeRegisterFeatureFlagDirective,
    AuthorizeRegisterFeatureFlagInvertedDirective,
    AuthorizeShowDirective,
    AutocalcLifeComponent,
    AutocalcLifeEndDateComponent,
    AzureMarketPlaceComponent,
    BackgroundProgressDirective,
    CaClass29MethodModifierComponent,
    CalendarSelectorComponent,
    ConfirmationModalComponent,
    ConfirmChangePlanModalComponent,
    ControlEnableContainerDirective,
    ControlEnableControlDirective,
    ControlEnableControlNameDirective,
    CreateFirstOrganisationComponent,
    CreateOrganisationConfirmModalComponent,
    CreateRegisterModalComponent,
    CurrencyInputComponent,
    CustomFieldsEditComponent,
    CustomFieldsEditSidePanelComponent,
    CustomFieldsViewComponent,
    DateInputComponent,
    DemoBannerComponent,
    DepreciationLookupComponent,
    DepreciationLookupModalComponent,
    DepreciationLookupSearchItemComponent,
    DepreciationLookupSearchItemSelectComponent,
    DepreciationMethodModifierComponent,
    DepreciationMethodSelectorComponent,
    DepreciationRuleSelectorComponent,
    DraftAssetListActionCellComponent,
    DraftAssetListLinkCellComponent,
    DragSortDirective,
    EditAssetDetailsComponent,
    EditAttachmentLinkModalComponent,
    EditFieldAllocationsComponent,
    EditOrganisationModalComponent,
    EnumToArrayPipe,
    ExpiredOrganisationInviteComponent,
    ExportButtonComponent,
    ExportModalComponent,
    FeatureLimitModalComponent,
    FilterArgumentPipe,
    FilterBarComponent,
    FinYearSelectorComponent,
    FlagInputDirective,
    FormControlClassDirective,
    FormControlComponent,
    FormLabelComponent,
    FySelectorComponent,
    GlAccountTypeaheadComponent,
    GridColumnComponent,
    GridColumnGroupComponent,
    GridColumnHeaderComponent,
    GridExpandCollapseButtonComponent,
    GridExpandCollapseButtonDirective,
    GridExportButtonDirective,
    GridExportButtonDirective,
    GridRowDataDirective,
    GridRowLinkComponent,
    GridTableComponent,
    GuideBannerComponent,
    HeaderBarComponent,
    InputNumberDirective,
    IntuitCopyrightComponent,
    InvalidMessageForDirective,
    IzePipe,
    KeepHtmlPipe,
    ListPipe,
    LoadingComponent,
    MaxDateValidator,
    MinDateValidator,
    MonthYearDateSelectorComponent,
    NameCellComponent,
    NavigationV2Component,
    NewAssetDetailsComponent,
    NewAssetFieldsFormComponent,
    ObserveResizeDirective,
    OffersComponent,
    OnboardingQuestionsComponent,
    OutageComponent,
    PaymentComponent,
    PaymentRequiredComponent,
    PercentageInputComponent,
    PeriodSelectorComponent,
    PoolSettingSelectionComponent,
    PoolTransactionDescriptionCellComponent,
    PrecisionDirective,
    PricingPackageActionsComponent,
    ProgressModalComponent,
    ProgressRingComponent,
    QuotePipe,
    RegimeSelectorComponent,
    RegimesInputComponent,
    RegisterListComponent,
    ReleaseNotesModalComponent,
    ReviewsComponent,
    SearchAssetsNameCellComponent,
    SearchBarComponent,
    SelectPeriodModalComponent,
    ServerRowDataDirective,
    SettingsBannerComponent,
    SidePanelComponent,
    SimpleModalComponent,
    StandardPageActionsComponent,
    StandardPageComponent,
    StandardPageContentActionsComponent,
    StandardPageContentComponent,
    StandardPageContentGridComponent,
    StartBannerComponent,
    StartComponent,
    StatusCellComponent,
    StoredReportListComponent,
    SubNavigationComponent,
    SubscriptionCalculatorComponent,
    SubscriptionLimitComponent,
    SubscriptionPackagesComponent,
    SwitchOrganisationModalComponent,
    TabHasChangesDirective,
    TabRouteDirective,
    TaxOfficeAbbrComponent,
    TrackingEventDirective,
    TransactionDescriptionComponent,
    UpdateModalComponent,
    ValidationContextDirective,
    ValidSubmitDirective,
    ValueFormatDirective,
    ViewAssetTransactionsComponent,
    ViewAttachmentsComponent,
    ViewPoolAssetTransactionsComponent,
    ViewPricingModalComponent,
    VirtualConfigDirective,
  ],
  providers: [
    {
      provide: DEPRECIATION_METHOD_MODIFIER,
      useValue: CaClass29MethodModifierComponent,
      multi: true,
    },
    {
      provide: SEARCH_EQUALITY,
      useFactory: searchEqualityFactory,
      deps: [LOCALE_ID],
    },
    {
      provide: SORT_COMPARE,
      useFactory: sortCompareFactory,
      deps: [LOCALE_ID],
    },
    {
      provide: UrlSerializer,
      useClass: AAUrlSerializer,
    },
    { provide: LUCIDE_ICONS, multi: true, useValue: new LucideIconProvider(customIcons) },
  ],
  exports: [
    AaCurrencyPipe,
    AaDatePipe,
    AaDateRangePipe,
    AaFinYearPipe,
    AaListDatePipe,
    AaPercentPipe,
    AlertBannerComponent,
    AlertComponent,
    AssetGroupSelectorComponent,
    AuthorizeEnableControlDirective,
    AuthorizeEnableDirective,
    AuthorizeEnableNavItemDirective,
    AuthorizeFeatureDirective,
    AuthorizeFeatureFlagDirective,
    AuthorizeOrganisationAnyDirective,
    AuthorizeOrganisationDirective,
    AuthorizeRegisterAnyDirective,
    AuthorizeRegisterDirective,
    AuthorizeRegisterFeatureFlagDirective,
    AuthorizeRegisterFeatureFlagInvertedDirective,
    AuthorizeShowDirective,
    BackgroundProgressDirective,
    CalendarSelectorComponent,
    CommonModule,
    ControlEnableContainerDirective,
    ControlEnableControlDirective,
    ControlEnableControlNameDirective,
    CreateFirstOrganisationComponent,
    CreateRegisterModalComponent,
    CurrencyInputComponent,
    CustomFieldsEditComponent,
    CustomFieldsViewComponent,
    DateInputComponent,
    DemoBannerComponent,
    DepreciationMethodModifierComponent,
    DepreciationMethodSelectorComponent,
    DepreciationRuleSelectorComponent,
    DraftAssetListActionCellComponent,
    DraftAssetListLinkCellComponent,
    DragSortDirective,
    EditAssetDetailsComponent,
    EditFieldAllocationsComponent,
    EnumToArrayPipe,
    ExportButtonComponent,
    FilterArgumentPipe,
    FilterBarComponent,
    FinYearSelectorComponent,
    FlagInputDirective,
    FormControlClassDirective,
    FormControlComponent,
    FormLabelComponent,
    FormsModule,
    GlAccountTypeaheadComponent,
    GridColumnComponent,
    GridColumnGroupComponent,
    GridColumnHeaderComponent,
    GridExpandCollapseButtonComponent,
    GridExpandCollapseButtonDirective,
    GridExportButtonDirective,
    GridExportButtonDirective,
    GridRowDataDirective,
    GridRowLinkComponent,
    GridTableComponent,
    GuideBannerComponent,
    HeaderBarComponent,
    IntuitCopyrightComponent,
    InvalidMessageForDirective,
    IzePipe,
    KeepHtmlPipe,
    ListPipe,
    LoadingComponent,
    LucideAngularModule,
    MaxDateValidator,
    MinDateValidator,
    MomentModule,
    MonthYearDateSelectorComponent,
    NameCellComponent,
    NavigationV2Component,
    NewAssetDetailsComponent,
    NewAssetFieldsFormComponent,
    NgbModule,
    ObserveResizeDirective,
    OutageComponent,
    PaymentComponent,
    PaymentRequiredComponent,
    PercentageInputComponent,
    PeriodSelectorComponent,
    PoolSettingSelectionComponent,
    PoolTransactionDescriptionCellComponent,
    PrecisionDirective,
    QuotePipe,
    ReactiveFormsModule,
    RegimeSelectorComponent,
    RegimesInputComponent,
    RegisterListComponent,
    RouterModule,
    ScrollingModule,
    SearchAssetsNameCellComponent,
    ServerRowDataDirective,
    SettingsBannerComponent,
    SidePanelComponent,
    StandardPageActionsComponent,
    StandardPageComponent,
    StandardPageContentActionsComponent,
    StandardPageContentComponent,
    StandardPageContentGridComponent,
    StartBannerComponent,
    StatusCellComponent,
    StoredReportListComponent,
    SubNavigationComponent,
    SubscriptionCalculatorComponent,
    SubscriptionLimitComponent,
    SubscriptionPackagesComponent,
    TabHasChangesDirective,
    TabRouteDirective,
    TaxOfficeAbbrComponent,
    TransactionDescriptionComponent,
    ValidationContextDirective,
    ValidSubmitDirective,
    ValueFormatDirective,
    ViewAssetTransactionsComponent,
    ViewAttachmentsComponent,
    ViewPoolAssetTransactionsComponent,
    ViewPricingModalComponent,
    VirtualConfigDirective,
  ],
})
export class CoreModule {}
